import { gql } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";

import {
  OrganizationalUnitsQuery,
  useCreateOrganizationalUnitMutation,
  useRenameOrganizationalUnitMutation,
  useSetOrganizationalUnitActiveMutation,
} from "~/graphql/__generated__/graphql";
import { useSnackbar } from "~/hooks/useSnackbar";

type OrganizationalUnit = OrganizationalUnitsQuery["organizationalUnit"][number];

interface OrganizationalUnitRowProps {
  organizationalUnit: OrganizationalUnit;
}

const OrganizationalUnitRow = ({ organizationalUnit }: OrganizationalUnitRowProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [setActive, { loading }] = useSetOrganizationalUnitActiveMutation({
    refetchQueries: ["OrganizationalUnits", "OrganizationProviderConfig"],
  });
  const { setErrorMessage } = useSnackbar();

  const toggleActive = () => {
    const nextActive = !organizationalUnit.active;
    setActive({
      variables: {
        id: organizationalUnit.id,
        active: nextActive,
      },
      optimisticResponse: {
        updateOrganizationalUnit: {
          ...organizationalUnit,
          active: nextActive,
          __typename: "OrganizationalUnitSelectItem",
        },
      },
    }).catch(() => {
      setErrorMessage("Organizational unit could not be updated. Please try again.");
    });
  };

  return (
    <TableRow>
      <TableCell>
        {organizationalUnit.name}
        <IconButton
          aria-label={`Edit ${organizationalUnit.name}`}
          size="small"
          onClick={() => setDialogOpen(true)}
          sx={{ minWidth: "auto", padding: "4px" }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell>
        {organizationalUnit.active ? (
          <Chip label="Active" color="success" size="small" />
        ) : (
          <Chip label="Retired" size="small" />
        )}
      </TableCell>
      <TableCell>
        <Switch
          checked={organizationalUnit.active}
          disabled={loading}
          inputProps={{
            "aria-label": `${organizationalUnit.active ? "Retire" : "Restore"} ${organizationalUnit.name}`,
          }}
          onChange={toggleActive}
          size="small"
        />
      </TableCell>

      {dialogOpen && (
        <OrganizationalUnitDialog
          organizationalUnit={organizationalUnit}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </TableRow>
  );
};

export default OrganizationalUnitRow;

interface OrganizationalUnitDialogProps {
  onClose: () => void;
  organizationalUnit?: OrganizationalUnit;
}

export const OrganizationalUnitDialog = ({
  onClose,
  organizationalUnit,
}: OrganizationalUnitDialogProps) => {
  const [name, setName] = useState(organizationalUnit?.name ?? "");
  const [create, { loading: createLoading }] = useCreateOrganizationalUnitMutation({
    refetchQueries: ["OrganizationalUnits", "OrganizationProviderConfig"],
  });
  const [rename, { loading: updateLoading }] = useRenameOrganizationalUnitMutation({
    refetchQueries: ["OrganizationalUnits", "OrganizationProviderConfig"],
  });
  const { setErrorMessage } = useSnackbar();

  const loading = createLoading || updateLoading;
  const isEditing = !!organizationalUnit;

  const handleSave = async () => {
    const trimmedName = name.trim();

    try {
      if (organizationalUnit) {
        await rename({
          variables: {
            id: organizationalUnit.id,
            name: trimmedName,
          },
        });
      } else {
        await create({
          variables: { name: trimmedName },
          awaitRefetchQueries: true,
        });
      }

      onClose();
    } catch {
      setErrorMessage("Organizational unit could not be saved. Please try again.");
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? "Edit Organizational Unit" : "Add Organizational Unit"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          variant="outlined"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          loading={loading}
          disabled={!name.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

gql`
  mutation CreateOrganizationalUnit($name: String!) @permission(requires: manageOrgConfig) {
    createOrganizationalUnit(name: $name) {
      id
    }
  }
`;

gql`
  mutation RenameOrganizationalUnit($id: Int!, $name: String!) @permission(requires: manageOrgConfig) {
    updateOrganizationalUnit(id: $id, name: $name) {
      id
      name
      active
    }
  }
`;

gql`
  mutation SetOrganizationalUnitActive($id: Int!, $active: Boolean!) @permission(requires: manageOrgConfig) {
    updateOrganizationalUnit(id: $id, active: $active) {
      id
      name
      active
    }
  }
`;
